import React, { use, useEffect } from "react";
import MuxPlayer from "@mux/mux-player-react";
import Mux from "@mux/mux-node";
import "leaflet/dist/leaflet.css";
import {
  Drawer,
  Button,
  Typography,
  IconButton,
  Box,
  Divider,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReportIssue from "./ReportIssue";
import AskQuestion from "./AskQuestion";
import IssueDetails from "./IssueDetails";
import { DrawerContentType, useDrawerContext } from "./DrawerContext";
import { useNavigate } from "react-router-dom";
import { useMapContext } from "./MapContext";

interface CustomDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  streamData: Mux.Video.LiveStreams.LiveStream | null;
  onStartStream: () => void;
  onGetStream: (streamId: string) => void;
  onCompleteStream: () => void;
}

const CustomDrawer = (props: CustomDrawerProps) => {
  const navigate = useNavigate();
  const { drawerContentType } = useDrawerContext();
  const [streamId, setStreamId] = React.useState("");
  const streamActive = !!props.streamData;
  const { selectedDocument } = useMapContext();

  const drawerTitle =
    drawerContentType === DrawerContentType.REPORT_ISSUE
      ? "Report an Issue"
      : drawerContentType === DrawerContentType.ASK_QUESTION
      ? "Ask a Question"
      : drawerContentType === DrawerContentType.VIEW_DETAILS
      ? "Issue Details"
      : "Unknown";
  const liveStreamPlaybackId = props.streamData?.playback_ids?.[0]?.id;

  // Update the URL when the drawer is opened for VIEW_DETAILS
  useEffect(() => {
    if (
      props.open &&
      drawerContentType === DrawerContentType.VIEW_DETAILS &&
      selectedDocument?.id
    ) {
      navigate(`/issue/${selectedDocument.id}`);
    }
  }, [props.open, drawerContentType, selectedDocument, navigate]);

  // Reset the URL when the drawer is closed
  useEffect(() => {
    if (!props.open) {
      navigate("/");
    }
  }, [props.open, navigate]);

  const handleSubmitLoadStream = async (e: React.FormEvent) => {
    e.preventDefault();
    await props.onGetStream(streamId);
    setStreamId("");
  };

  return (
    <Drawer
      anchor="right"
      open={props.open}
      onClose={props.onClose}
      sx={{
        width: 300,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: 300,
          padding: 2,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Drawer Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">{drawerTitle}</Typography>
        <IconButton onClick={() => props.onClose()}>
          <CloseIcon />
        </IconButton>
      </Box>
      {drawerContentType === DrawerContentType.REPORT_ISSUE && (
        <Box>
          {!streamActive && (
            <Box>
              <Box>
                Start or load a live stream to record the incident with footage
                from the live stream included.
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={props.onStartStream}
              >
                Start Stream
              </Button>
              <form onSubmit={handleSubmitLoadStream}>
                <TextField
                  label="Stream ID"
                  value={streamId}
                  onChange={(e) => setStreamId(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                />
                <Button type="submit" variant="contained" color="secondary">
                  Load Stream
                </Button>
              </form>
              <Divider sx={{ my: 2 }} />
            </Box>
          )}

          {/* TODO: Do some typescript magic so streamActive already takes care of check that props.streamData exists */}
          {streamActive && props.streamData && (
            <Box>
              <Typography variant="h6">Stream Information</Typography>
              <Box>
                Copy this following information into the DJI app to start
                streaming.
              </Box>
              <Box>RTMP URL: rtmp://global-live.mux.com:5222/app/</Box>
              <Box>Stream Key: {props.streamData["stream_key"]}</Box>
              <Box>Stream ID: {props.streamData["id"]}</Box>

              <MuxPlayer
                playbackId={liveStreamPlaybackId}
                metadata={{
                  video_id: props.streamData["id"],
                  video_title: "Live Stream",
                }}
              />
              <Box>
                <a
                  href={`https://player.mux.com/${liveStreamPlaybackId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Watch Page
                </a>
              </Box>

              <Button
                variant="contained"
                color="secondary"
                onClick={props.onCompleteStream}
              >
                Complete Stream
              </Button>
            </Box>
          )}
          <ReportIssue
            streamId={props.streamData?.id}
            onSuccess={() => {
              props.onSuccess();
              props.onClose();
            }}
          />
        </Box>
      )}
      {drawerContentType === DrawerContentType.ASK_QUESTION && <AskQuestion />}
      {drawerContentType === DrawerContentType.VIEW_DETAILS && <IssueDetails />}
    </Drawer>
  );
};

export default CustomDrawer;
